import { Track } from "@/src/lib/tracking";
import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import {
  useState,
  type FC,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import Image from "next/image";
import { dayjs, cn } from "utils";
import { useMemoizedFn } from "ahooks";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Chat/ShareContent";
import { SharePage } from "@screens/SharePage";
import { useIsPc } from "@hooks/useIsPc";
import { logEventAnalytics } from "@features/firebase";
import styles from "./sharePreview.module.css";

const I18n = I18nUtil.createScoped(Translations);

// Constants
const IMAGE_QUALITY = 1;
const PIXEL_RATIO = 2;
const DEFAULT_FILENAME = "ChatShare-Edgen";
// 图片对话区域最大高度
const MAX_HEIGHT = 5807;

// Types
interface ShareContentProps {
  shareUrl: string;
  onClose?: () => void;
}

interface ImageGenerationOptions {
  quality: number;
  pixelRatio: number;
  backgroundColor: string;
  style: {
    transform: string;
    transformOrigin: string;
  };
  filter: (node: Node | null) => boolean;
  cacheBust: boolean;
  skipFonts: boolean;
  skipAutoScale: boolean;
}

// Utility functions
const createTempContainer = (originalElement: HTMLElement): HTMLDivElement => {
  const tempContainer = document.createElement("div");
  const originalStyle = window.getComputedStyle(originalElement);

  tempContainer.style.cssText = `
    position: fixed;
    left: 0;
    top: 0;
    width: ${originalElement.offsetWidth}px;
    height: ${originalElement.scrollHeight}px;
    background: ${originalStyle.backgroundColor};
    pointer-events: none;
    z-index: -1;
  `;

  return tempContainer;
};

const cloneElementWithStyles = (element: HTMLElement): HTMLElement => {
  const clone = element.cloneNode(true) as HTMLElement;
  const originalStyle = window.getComputedStyle(element);

  // 保持原始尺寸和间距
  clone.style.cssText = `
    width: ${originalStyle.width};
    background: ${originalStyle.background};
    padding: ${originalStyle.padding};
    margin: ${originalStyle.margin};
    border: ${originalStyle.border};
    border-radius: ${originalStyle.borderRadius};
    box-shadow: ${originalStyle.boxShadow};
    height: auto;
    max-height: none;
    overflow: visible;
    position: relative;
    display: ${originalStyle.display};
    flex-direction: ${originalStyle.flexDirection};
    align-items: ${originalStyle.alignItems};
    justify-content: ${originalStyle.justifyContent};
    gap: ${originalStyle.gap};
    color: ${originalStyle.color};
    font-family: ${originalStyle.fontFamily};
    font-size: ${originalStyle.fontSize};
    line-height: ${originalStyle.lineHeight};
    transform: scale(1);
    transform-origin: top left;
  `;

  return clone;
};

const waitForImages = async (container: HTMLElement): Promise<void> => {
  const images = container.getElementsByTagName("img");
  if (images.length === 0) {
    return;
  }

  await Promise.all(
    Array.from(images).map(
      (img, index) =>
        new Promise<void>((resolve) => {
          // Skip favicon and other potentially problematic images
          if (
            img.src.includes("favicon.ico") ||
            img.classList.contains("no-capture") ||
            !img.src // Skip images with empty src
          ) {
            img.style.display = "none";
            img.remove();
            resolve();
            return;
          }

          // If image is already loaded or has an error, resolve immediately
          if (img.complete) {
            if (img.naturalWidth === 0) {
              // Image failed to load
              img.style.display = "none";
              img.remove();
              Track.log(
                "warn",
                "ShareContent",
                `Image ${index + 1} failed to load (already complete):`,
                {
                  src: img.src,
                  classes: img.className,
                },
              );
            }
            resolve();
            return;
          }

          // Set a timeout to prevent hanging
          const timeout = setTimeout(() => {
            img.style.display = "none";
            img.remove();
            Track.log(
              "warn",
              "ShareContent",
              `Image ${index + 1} loading timed out:`,
              {
                src: img.src,
                classes: img.className,
              },
            );
            resolve();
          }, 5000);

          const handleLoad = () => {
            clearTimeout(timeout);
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);
            resolve();
          };

          const handleError = (error: Event | string) => {
            clearTimeout(timeout);
            img.style.display = "none";
            img.remove();
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);

            // Log the error but continue with the process
            Track.log(
              "warn",
              "ShareContent",
              `Image ${index + 1} failed to load:`,
              {
                src: img.src,
                classes: img.className,
                error: error instanceof Event ? "Image load error" : error,
              },
            );
            resolve();
          };

          // Add error handler first to catch any immediate errors
          img.addEventListener("error", handleError);
          img.addEventListener("load", handleLoad);

          // If the image is already loaded, resolve immediately
          if (img.complete) {
            handleLoad();
          }
        }),
    ),
  );
};

const getImageGenerationOptions = (
  container: HTMLElement,
): ImageGenerationOptions => ({
  quality: IMAGE_QUALITY,
  pixelRatio: PIXEL_RATIO,
  backgroundColor: window.getComputedStyle(container).backgroundColor,
  style: {
    transform: "none",
    transformOrigin: "top left",
  },
  filter: (node) => {
    if (!node || typeof (node as HTMLElement).className !== "string") {
      return true;
    }
    // Skip problematic images and elements with no-capture class
    if (node instanceof HTMLImageElement) {
      return !(
        node.src.includes("favicon.ico") ||
        node.classList.contains("no-capture") ||
        node.style.display === "none"
      );
    }
    return !(node as HTMLElement).className.includes("no-capture");
  },
  cacheBust: true,
  skipFonts: false,
  skipAutoScale: false,
});

const downloadImage = (dataUrl: string, filename: string) => {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
  URL.revokeObjectURL(dataUrl);
};

const getErrorMessages = () =>
  ({
    GENERATION_FAILED: I18n.t("Image generation failed"),
    CLIPBOARD_DENIED: I18n.t("Clipboard access denied"),
    REF_NOT_AVAILABLE: I18n.t("Share content reference not available"),
    COPY_SUCCESS: I18n.t("The picture was copy successfully."),
    DOWNLOAD_SUCCESS: I18n.t("The picture was downloaded successfully."),
  }) as const;
export const ShareContent: FC<ShareContentProps> = ({ shareUrl, onClose }) => {
  const [operable, setOperable] = useState<"copy" | "download" | "">("");
  const shareContentRef = useRef<HTMLDivElement>(null);
  const sharePageRef = useRef<HTMLDivElement>(null);
  const [isOverflowContent, setIsOverflowContent] = useState(false);
  const [isPC] = useIsPc();

  // 新增：初始化时禁用操作，1s后再根据 operable 状态赋值
  const [initDisable, setInitDisable] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setInitDisable(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const isDisableOperable = initDisable || !!operable;

  const id = useMemo(
    () => (shareUrl ? new URL(shareUrl).pathname.split("/").pop() || "" : ""),
    [shareUrl],
  );

  const formattedDate = useMemo(
    () => dayjs(new Date()).format("MMM DD YYYY, HH:mm"),
    [],
  );

  useEffect(() => {
    const checkHeight = () => {
      if (sharePageRef.current) {
        const height = sharePageRef.current.scrollHeight;
        setIsOverflowContent(height > MAX_HEIGHT);
      }
    };

    checkHeight();
    const resizeObserver = new ResizeObserver(checkHeight);
    if (sharePageRef.current) {
      resizeObserver.observe(sharePageRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleImageOperation = useMemoizedFn(
    async (container: HTMLElement, isCopy: boolean) => {
      if (!container) {
        Track.log("error", "ShareContent", "Container element is undefined");
        return;
      }
      const { toPng, toBlob } = await import("html-to-image");

      let tempContainer: HTMLDivElement | null = null;
      try {
        tempContainer = createTempContainer(container);
        const clone = cloneElementWithStyles(container);

        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        // Wait for images and remove failed ones
        await waitForImages(tempContainer);

        const options = getImageGenerationOptions(tempContainer);

        if (isCopy) {
          try {
            const blob = await toBlob(tempContainer, {
              ...options,
              canvasWidth: container.offsetWidth * PIXEL_RATIO,
              canvasHeight: container.offsetHeight * PIXEL_RATIO,
              style: {
                transform: "scale(1)",
                transformOrigin: "top left",
              },
            });

            if (!blob) {
              throw new Error(getErrorMessages().GENERATION_FAILED);
            }

            try {
              const item = new ClipboardItem({ "image/png": blob });
              await navigator.clipboard.write([item]);
              logEventAnalytics("share_chat_copy_success");
              OspToast.success({
                content: getErrorMessages().COPY_SUCCESS,
              });
              onClose?.();
            } catch (error) {
              const dataUrl = URL.createObjectURL(blob);
              downloadImage(dataUrl, "ChatShare-Edgen");
              OspToast.info({
                content: getErrorMessages().CLIPBOARD_DENIED,
              });
              Track.log(
                "error",
                "ShareContent",
                `${getErrorMessages().CLIPBOARD_DENIED}`,
                { cause: String(error) },
              );
            }
          } catch (error) {
            // If blob generation fails, try to generate PNG directly
            const dataUrl = await toPng(tempContainer, options);
            downloadImage(dataUrl, "ChatShare-Edgen");
            OspToast.info({
              content: getErrorMessages().CLIPBOARD_DENIED,
            });
            Track.log(
              "error",
              "ShareContent",
              `${getErrorMessages().CLIPBOARD_DENIED} toBlob`,
              { cause: String(error) },
            );
          }
        } else {
          const dataUrl = await toPng(tempContainer, options);
          downloadImage(dataUrl, DEFAULT_FILENAME);
          logEventAnalytics("share_chat_download_success");
          OspToast.success({
            content: getErrorMessages().DOWNLOAD_SUCCESS,
          });
          onClose?.();
        }
      } catch (error) {
        Track.log(
          "error",
          "ShareContent",
          `${isCopy ? "Copy" : "Download"} image generation failed:`,
          { cause: String(error) },
        );
        // Even if there's an error, try to generate a basic version without problematic images
        try {
          if (tempContainer) {
            const basicOptions = {
              ...getImageGenerationOptions(tempContainer),
              filter: (node: Node | null) => {
                if (
                  !node ||
                  typeof (node as HTMLElement).className !== "string"
                ) {
                  return true;
                }
                // Skip all images that might cause issues
                if (node instanceof HTMLImageElement) {
                  return false;
                }
                return !(node as HTMLElement).className.includes("no-capture");
              },
            };

            if (isCopy) {
              // Try to copy the simplified version
              const blob = await toBlob(tempContainer, basicOptions);
              if (blob) {
                try {
                  const item = new ClipboardItem({ "image/png": blob });
                  await navigator.clipboard.write([item]);
                  OspToast.success({
                    content: I18n.t("Copied simplified version successfully"),
                  });
                  return;
                } catch {
                  // If copy fails, fall back to download
                  const dataUrl = URL.createObjectURL(blob);
                  downloadImage(dataUrl, "ChatShare-Edgen");
                  const ERROR_MESSAGES = getErrorMessages();
                  OspToast.info({
                    content: ERROR_MESSAGES.CLIPBOARD_DENIED,
                  });
                }
              }
            } else {
              // For download operation, proceed with download
              const dataUrl = await toPng(tempContainer, basicOptions);
              downloadImage(dataUrl, "ChatShare-Edgen");
            }
          }
        } catch (fallbackError) {
          OspToast.error({
            content: I18n.t("The picture %{var0} failed.", {
              var0: isCopy ? "copy" : "download",
            }),
          });
          logEventAnalytics(
            isCopy ? "share_chat_copy_failed" : "share_chat_download_failed",
          );
          Track.log(
            "error",
            "ShareContent",
            `${
              isCopy ? "share_chat_copy_failed" : "share_chat_download_failed"
            }`,
            { cause: String(fallbackError) },
          );
        }
      } finally {
        if (tempContainer && tempContainer.parentNode) {
          tempContainer.parentNode.removeChild(tempContainer);
        }
      }
    },
  );

  const handleCopyImage = useMemoizedFn(async () => {
    if (!shareContentRef.current) {
      return;
    }
    logEventAnalytics("share_chat_copy_start");

    setOperable("copy");
    try {
      await handleImageOperation(shareContentRef.current, true);
    } finally {
      setOperable("");
    }
  });

  const handleDownloadImage = useCallback(async () => {
    if (!shareContentRef.current) {
      return;
    }
    logEventAnalytics("share_chat_download_start");
    setOperable("download");
    try {
      await handleImageOperation(shareContentRef.current, false);
    } finally {
      setOperable("");
    }
  }, [handleImageOperation]);

  const renderButtons = useCallback(
    () => (
      <div className="flex gap-4 justify-end pointer-events-auto">
        <button
          type="button"
          data-openreplay-label="chatbox_sharecontent_copy"
          className={cn(
            "button group flex-1 md:flex-none min-w-[120px]",
            isPC ? "sm_button" : "md_button",
            "primary_button",
          )}
          disabled={isDisableOperable}
          data-loading={operable === "copy"}
          onClick={handleCopyImage}
        >
          <span className="flex items-center gap-1 button_m group-loading:hidden">
            <Icon name="copy" size={20} />
            <span>{I18n.t("Copy image")}</span>
          </span>
          <Icon
            name="ic_loading2"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className={cn("hidden animate-spin group-loading:block")}
          />
        </button>
        <button
          type="button"
          data-openreplay-label="chatbox_sharecontent_share"
          className={cn(
            "button group flex-1 md:flex-none min-w-[120px]",
            isPC ? "sm_button" : "md_button",
            "primary_button",
          )}
          disabled={isDisableOperable}
          data-loading={operable === "download"}
          onClick={handleDownloadImage}
        >
          <span className="flex items-center gap-1 button_m group-loading:hidden">
            <Icon name="upload_dapp" size={20} />
            <span>{I18n.t("Download")}</span>
          </span>
          <Icon
            name="ic_loading2"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className={cn("hidden animate-spin group-loading:block")}
          />
        </button>
      </div>
    ),
    [isPC, operable, isDisableOperable, handleCopyImage, handleDownloadImage],
  );

  return (
    <div
      className={cn(
        styles.previewRoot,
        "pointer-events-none flex w-full min-w-0 max-w-full flex-col gap-6",
      )}
    >
      <div className="pointer-events-auto h-[480px] max-w-full min-w-0 overflow-y-auto rounded-2xl bg-background_solid_secondary no-scrollbar md:h-[564px]">
        <div
          ref={shareContentRef}
          className="pointer-events-none min-w-0 max-w-full rounded-2xl bg-background_solid_secondary"
        >
          <div className="py-[35px] px-6 bg-background_solid_secondary rounded-t-2xl flex justify-between items-center bg-[length:100%_100%] bg-center bg-no-repeat">
            <Image
              src="/edgen_logo_full_v2.svg"
              alt=""
              width={56}
              height={28}
              className="h-7"
            />
            <div className="body_medium_xl text-gradient">
              {I18n.t("Your AI CIO")}
            </div>
          </div>
          <div className="md:px-2 px-0 pt-1">
            <div
              ref={sharePageRef}
              className={cn("min-w-0 max-w-full", {
                "overflow-hidden": isOverflowContent,
              })}
              style={{
                maxHeight: `${MAX_HEIGHT}px`,
                "--token-line-header-flex-direction": "column",
                "--token-line-header-align-items": "flex-start",
                "--token-line-header-gap": "8px",
                "--token-container-height": isPC ? "602px" : "690px",
                "--token-radar-container-min-width": "270px",
                "--token-price-padding-x": "8px",
              }}
            >
              <div className="body_medium_m text-content_tertiary mt-5 px-4">
                {formattedDate}
              </div>
              <SharePage
                id={id}
                isOnlyChat={true}
                className={cn(styles.previewChat, "min-h-[400px]", {
                  "overflow-hidden": isOverflowContent,
                })}
              />
            </div>
          </div>
          <div
            className={cn(
              "px-6 py-6 rounded-b-2xl flex flex-row justify-center border-t border-border_white_transparent10",
              {
                "mt-4": isOverflowContent,
              },
            )}
          >
            <div className="flex items-center body_medium_m text-content_tertiary gap-1">
              {I18n.t("For more information, visit")}
              <span className="body_medium_m text-content_primary">
                {I18n.t("Edgen")}
              </span>
            </div>
          </div>
        </div>
      </div>
      {renderButtons()}
    </div>
  );
};
